import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Views } from "react-big-calendar";
import { formatTime12, formatTime12Compact } from "@/utils/date-format";
import { useResourceCalendarView } from "@/components/resource-big-calendar/ResourceCalendarViewContext";
import {
  eventNeedsAnySadarat,
  formatRazaEventSadaratListLabel,
} from "@/utils/format-raza-event-sadarat-display";
import { shouldFlagMissingSadarat } from "../missing-sadarat-highlight";
import type { RazaEventsCalendarEvent } from "./types";

function buildFullTooltip(event: RazaEventsCalendarEvent): string {
  const time = `${formatTime12(event.start)} – ${formatTime12(event.end)}`;
  if (event.overlayKind === "hall") {
    const parts = [time, "Hall", event.title, event.subTitle];
    if (event.hallOverlay?.isBlockedDate) parts.push("Blocked");
    return parts.filter(Boolean).join(" · ");
  }
  const row = event.resource;
  const missingSadarat = row && shouldFlagMissingSadarat(row);
  const sadaratLabel =
    row && eventNeedsAnySadarat(row) ? formatRazaEventSadaratListLabel(row, { empty: "" }) : "";
  const parts = [time, event.title, event.subTitle];
  if (!missingSadarat && sadaratLabel) parts.push(`Sadarat: ${sadaratLabel}`);
  if (missingSadarat) parts.push("No sadarat");
  return parts.filter(Boolean).join(" · ");
}

export function RazaEventsCalendarEventComponent({ event }: { event: RazaEventsCalendarEvent }) {
  const muiTheme = useTheme();
  const view = useResourceCalendarView();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const isHallOverlay = event.overlayKind === "hall";
  const row = event.resource;
  const missingSadarat = !isHallOverlay && row && shouldFlagMissingSadarat(row);
  const sadaratLabel =
    !isHallOverlay && row && eventNeedsAnySadarat(row) && !missingSadarat
      ? formatRazaEventSadaratListLabel(row, { empty: "" })
      : "";
  const tooltip = buildFullTooltip(event);

  if (view === Views.MONTH) {
    const start = formatTime12Compact(event.start);
    const flag = missingSadarat ? "!" : "";
    const hallTag = isHallOverlay ? "H" : "";
    const line = [start, hallTag, event.title, flag].filter(Boolean).join(" · ");

    return (
      <Box
        title={tooltip}
        sx={{
          color: "inherit",
          px: 0,
          py: 0,
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          fontSize: isMobile ? "0.65rem" : "0.7rem",
          lineHeight: 1.2,
          fontWeight: missingSadarat || isHallOverlay ? 600 : 500,
        }}
      >
        {line}
      </Box>
    );
  }

  const secondarySx = {
    mt: 0.25,
    fontSize: isMobile ? "0.65rem" : "0.68rem",
    lineHeight: 1.25,
    opacity: 0.95,
    wordBreak: "break-word" as const,
  };

  return (
    <Box
      title={tooltip}
      sx={{
        color: "inherit",
        p: 0,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
        fontSize: isMobile ? "0.68rem" : "0.75rem",
        lineHeight: 1.25,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{
          fontWeight: 700,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {formatTime12(event.start)} – {formatTime12(event.end)}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {isHallOverlay ? `Hall · ${event.title}` : event.title}
      </Typography>
      {event.subTitle && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            opacity: 0.92,
          }}
        >
          {event.subTitle}
        </Typography>
      )}
      {!isHallOverlay && sadaratLabel && (
        <Box component="span" sx={secondarySx}>
          Sadarat: {sadaratLabel}
        </Box>
      )}
      {!isHallOverlay && missingSadarat && (
        <Box component="span" sx={{ ...secondarySx, fontStyle: "italic", opacity: 0.92 }}>
          No sadarat
        </Box>
      )}
    </Box>
  );
}
